import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowDown } from "lucide-react";
import logo from "@/assets/logo.png";
import { CONTACT } from "@/lib/site";

const HERO_BG =
  "https://images.unsplash.com/photo-1575917649705-5b59aaa12e6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grain"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#06040A]/85 via-[#06040A]/80 to-[#06040A]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(93,42,142,0.35),transparent_60%)]" />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-24 pb-16">
        <motion.img
          src={logo}
          alt="The Change Center Church Atlanta"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="h-44 sm:h-60 w-auto mx-auto mb-8 animate-float rounded-2xl border border-[#D4AF37]/40 shadow-[0_15px_50px_rgba(212,175,55,0.35)]"
          data-testid="hero-logo"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-xs sm:text-sm uppercase tracking-[0.45em] text-[#D4AF37] mb-5"
        >
          Welcome To
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.9 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-6"
        >
          <span className="gold-text">Faith · Family · Purpose</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="font-display text-2xl sm:text-3xl text-[#F9F6F0] mb-10 italic"
        >
          The Change Center Church · Atlanta
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-12"
        >
          <div className="flex items-center gap-2 text-[#A19CA8]" data-testid="hero-location">
            <MapPin className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-sm tracking-wide">{CONTACT.location}</span>
          </div>
          <div className="hidden sm:block h-5 w-px bg-[#D4AF37]/30" />
          <div className="flex items-center gap-2 text-[#A19CA8]" data-testid="hero-launch">
            <Calendar className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-sm tracking-wide">Launching {CONTACT.launchWindow}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#involved"
            data-testid="hero-involved-btn"
            className="w-full sm:w-auto bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-9 py-4 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.35)]"
          >
            I Want To Serve
          </a>
          <a
            href="#vendor"
            data-testid="hero-vendor-btn"
            className="w-full sm:w-auto bg-[#5D2A8E] text-white font-semibold uppercase tracking-wider text-sm px-9 py-4 hover:bg-[#7236AD] transition-all duration-300 border border-[#7236AD]"
          >
            Register for Events Here
          </a>
          <a
            href="#support"
            data-testid="hero-support-btn"
            className="w-full sm:w-auto border-2 border-[#D4AF37] text-[#D4AF37] font-semibold uppercase tracking-wider text-sm px-9 py-4 hover:bg-[#D4AF37]/10 transition-all duration-300"
          >
            Support The Vision
          </a>
        </motion.div>
      </div>

      <a
        href="#vision"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#D4AF37] animate-bounce"
        aria-label="Scroll down"
      >
        <ArrowDown className="h-6 w-6" />
      </a>
    </section>
  );
};
